import React, { forwardRef } from "react";
import { Box, MenuItem } from "@mui/material";
import { useTranslation } from "../../../localization/useTranslation";
import { DotLegalAlert, DotLegalHeader, DotLegalLink, DotLegalPrettyScrollbar } from "@dotlegal/dotlegal-ui-components";
import { EntityType } from "../../Common.types";
import { SearchResultViewModel } from "./SearchResultList.types.ts";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { getIcon } from "../../../taskManagement/taskPane/taskRelationsBox/TaskRelationsBox.tsx";
import { getLink } from "../../../taskManagement/taskPane/taskRelationsBox/TaskRelationsBox.hooks.ts";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { useNavigate } from "react-router";
import { useSearchNavigation } from "./useSearchNavigation.ts";

export interface SearchResultListContentProps {
    items: SearchResultViewModel[];
    isLoading: boolean;
    isLink?: boolean;
    isEntitySpecificSearch: boolean;
    onItemSelected: (mention: SearchResultViewModel) => void;
    onShowMoreClick: (entityType: EntityType | undefined) => void;
}

const SearchResultListContent = forwardRef((props: SearchResultListContentProps, ref: any) => {
    const { platformFeatures } = usePlanContext();
    const { items, isLoading, onItemSelected } = props;
    const { translateEntityType, translateString } = useTranslation();
    const urlProvider = useUrlProvider();
    const navigate = useNavigate();

    const itemsWithId = items.map((item) => ({ ...item, id: item.entityId }));

    const { selectedIndex, selectItem } = useSearchNavigation({
        items: itemsWithId,
        onSelect: (item) => {
            onItemSelected(item);
            if (props.isLink) {
                navigate(getLink(item.entityType, item.entityId, item.parentId, urlProvider, item.subjectEntityType, item.subjectEntityId));
            }
        },
        ref,
    });

    const getItemLink = (item: SearchResultViewModel) =>
        getLink(item.entityType, item.entityId, item.parentId, urlProvider, item.subjectEntityType, item.subjectEntityId);

    const renderLoadingState = () => <div>Loading...</div>;
    const renderEmptyState = () => <Box>No items found</Box>;
    const renderUpgradePlan = () => <div>{translateString("upgradePrivacyPlanToday")}</div>;

    const getMenuItem = (item: SearchResultViewModel, index: number) => {
        return (
            <MenuItem
                id={item.entityId}
                sx={(theme) => ({
                    paddingX: 2,
                    whiteSpace: "wrap",
                    "&:hover": {
                        backgroundColor: theme.palette.primary.light,
                    },
                })}
                onClick={() => (props.isLink ? {} : selectItem(index))}
                selected={index === selectedIndex}
            >
                <Box
                    sx={(theme) => ({
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: "center",
                            "& svg": {
                                width: 16,
                                height: 16,
                                fill: "#DAD9EF",
                            },
                        })}
                    >
                        {getIcon(item.entityType)}
                    </Box>
                    {item.entityName}
                </Box>
            </MenuItem>
        );
    };

    const renderItems = () => {
        const entityCounts: Record<EntityType, number> = {} as Record<EntityType, number>;
        let currentEntityType: EntityType | null = null;

        return (
            <>
                {props.isEntitySpecificSearch && items.length > 500 && (
                    <DotLegalAlert marginBottom={2} warningText={translateString("searchResultLimitReached")} />
                )}
                {props.onShowMoreClick && props.isEntitySpecificSearch && (
                    <DotLegalHeader headerStyle={"extraSmall"} marginBottom={0} marginTop={0}>
                        <DotLegalLink onClick={() => props.onShowMoreClick!(undefined)} linkColor={"primary"}>
                            {translateString("goBack")}
                        </DotLegalLink>
                    </DotLegalHeader>
                )}
                <DotLegalPrettyScrollbar sx={{ marginRight: -1 }}>
                    <Box sx={{ maxHeight: 300, marginRight: 1 }}>
                        {items
                            .map((item, index) => {
                                entityCounts[item.entityType] = (entityCounts[item.entityType] || 0) + 1;

                                if (entityCounts[item.entityType] > 5 && !props.isEntitySpecificSearch) {
                                    return null;
                                }

                                const isGroupedType = currentEntityType !== item.entityType;
                                currentEntityType = item.entityType;

                                const isLastOfType = entityCounts[item.entityType] === 5;
                                const hasMoreOfType = items.filter((i) => i.entityType === item.entityType).length > 5;

                                return (
                                    <React.Fragment key={`menuItem-${index}`}>
                                        {isGroupedType && (
                                            <DotLegalHeader headerStyle="small" marginTop={1} marginBottom={1}>
                                                {translateEntityType(EntityType[item.entityType])}
                                            </DotLegalHeader>
                                        )}
                                        {props.isLink ? (
                                            <Box onClick={() => props.onItemSelected(item)}>
                                                <DotLegalLink noUnderlineOnHover to={getItemLink(item)}>
                                                    {getMenuItem(item, index)}
                                                </DotLegalLink>
                                            </Box>
                                        ) : (
                                            getMenuItem(item, index)
                                        )}
                                        {isLastOfType && hasMoreOfType && props.onShowMoreClick && !props.isEntitySpecificSearch && (
                                            <Box sx={{ marginLeft: 2 }}>
                                                <DotLegalHeader headerStyle={"extraSmall"} marginBottom={0} marginTop={1}>
                                                    <DotLegalLink onClick={() => props.onShowMoreClick!(item.entityType)} linkColor={"primary"}>
                                                        {translateString("showMore")}
                                                    </DotLegalLink>
                                                </DotLegalHeader>
                                            </Box>
                                        )}
                                    </React.Fragment>
                                );
                            })
                            .filter(Boolean)}
                    </Box>
                </DotLegalPrettyScrollbar>
            </>
        );
    };

    const hasAnyResults = items.length > 0;

    return (
        <>
            {platformFeatures.canAccessRelationsBetweenEntities ? (
                <>{isLoading ? renderLoadingState() : !hasAnyResults ? renderEmptyState() : renderItems()}</>
            ) : (
                renderUpgradePlan()
            )}
        </>
    );
});

SearchResultListContent.displayName = "SearchResultListContent";

export default SearchResultListContent;
